import { Icon } from '@fortawesome/fontawesome-svg-core';
import { faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import {
  faCopy,
  faEllipsis,
  faEnvelope,
} from '@fortawesome/pro-regular-svg-icons';
import { faTrashCan } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Popover, Box, Modal } from '@mui/material';
import { useState } from 'react';
import { ActionModalContent } from './ActionModalContent';
import toast from 'react-hot-toast';
import { UserEmailModal } from './UserEmailModal';

export type Item = 'post' | 'comment';
export interface ActionMenuProps {
  showBlockUser: boolean;
  onDelete: () => void;
  onBlock: () => void;
  item: Item;
  userEmail?: string;
  userId?: number;
  username?: string;
  userProfilePicture?: string;
}

export const ActionMenu = ({
  showBlockUser,
  onDelete,
  onBlock,
  item,
  userEmail,
  userId,
  username,
  userProfilePicture,
}: ActionMenuProps) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLDivElement | null>(null);
  const [modalType, setModalType] = useState<'delete' | 'block'>('delete');
  const [isModalVisible, setModalVisible] = useState<boolean>(false);
  const [isUserEmailModalVisible, setUserEmailModalVisible] =
    useState<boolean>(false);

  const openMenu = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
  };

  const closeMenu = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    setMenuAnchorEl(null);
  };

  const deletePost = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onDelete();
    setModalVisible(false);
  };
  const onDismiss = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setModalVisible(false);
  };

  const openModal = (
    event: React.MouseEvent<HTMLDivElement>,
    type: 'delete' | 'block',
  ) => {
    event.stopPropagation();
    closeMenu(event);
    setModalType(type);
    setModalVisible(true);
  };

  const handleBlock = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onBlock();
    setModalVisible(false);
  };

  const openUserEmailModal = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    closeMenu(event);
    setUserEmailModalVisible(true);
  };

  return (
    <div data-testid="actionMenu">
      <Modal open={isModalVisible} onClose={onDismiss}>
        <>
          <ActionModalContent
            onSubmit={modalType === 'delete' ? deletePost : handleBlock}
            onDismiss={onDismiss}
            title={
              modalType === 'delete'
                ? `Are you sure you want to delete this ${item}?`
                : 'Are you sure you want to block this user?'
            }
            text={
              modalType === 'delete'
                ? undefined
                : 'This means that they will no longer be able to engage in your community.'
            }
            actionText={modalType === 'delete' ? 'Delete' : 'Block'}
          />
        </>
      </Modal>
      {isUserEmailModalVisible && userId && username && (
        <UserEmailModal
          open={isUserEmailModalVisible}
          onClose={() => setUserEmailModalVisible(false)}
          userId={userId}
          username={username}
          profilePicture={userProfilePicture}
          fallbackEmail={userEmail}
        />
      )}
      <div
        className="px-2.5 cursor-pointer hover:bg-[#6367ef0a]"
        onClick={openMenu}
        data-testid="actionMenuOpen"
      >
        <FontAwesomeIcon icon={faEllipsis} />
      </div>
      <Popover
        open={!!menuAnchorEl}
        onClose={closeMenu}
        anchorEl={menuAnchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box className="flex flex-col gap-2.5 p-3.5 justify-center items-start">
          <Box
            className="flex gap-1.5 justify-center items-center cursor-pointer"
            onClick={(ev) => openModal(ev, 'delete')}
            data-testid="actionMenuDelete"
          >
            <FontAwesomeIcon
              icon={faTrashCan}
              className="w-[20px] text-gray-3"
            />
            Delete
          </Box>
          {showBlockUser && (
            <Box
              className="flex gap-1.5 justify-center items-center cursor-pointer"
              onClick={(ev) => openModal(ev, 'block')}
              data-testid="actionMenuBlock"
            >
              <FontAwesomeIcon
                icon={faEyeSlash as Icon}
                className="text-gray-3"
              />
              Block
            </Box>
          )}
          {userId && username && (
            <Box
              className="flex gap-1.5 justify-center items-center cursor-pointer"
              onClick={openUserEmailModal}
              data-testid="actionMenuViewEmail"
            >
              <FontAwesomeIcon
                icon={faEnvelope}
                className="w-[20px] text-gray-3"
              />
              View email
            </Box>
          )}
          {userEmail && (
            <Box
              className="flex gap-1.5 justify-center items-center cursor-pointer"
              onClick={(event) => {
                event.stopPropagation();
                navigator.clipboard.writeText(userEmail);
                closeMenu(event);
                toast.success('Email copied to clipboard!');
              }}
              data-testid="actionMenuCopyEmail"
            >
              <FontAwesomeIcon icon={faCopy} className="w-[20px] text-gray-3" />
              Copy email
            </Box>
          )}
        </Box>
      </Popover>
    </div>
  );
};
